import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, TextField, Button, IconButton, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { v4 as uuid } from 'uuid';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { textFieldSx } from '../../utils/dropdownStyles';
import { hasRequiredName } from '../../utils/validators';

const SectionLabel = ({ children, required }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10, mb: 0.5 }}>
    <LocalText>{children}</LocalText>
    {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
  </Typography>
);

const CreateAdvertiserModal = ({ open, onClose, onSave, initial, readOnly = false }) => {
  const [nameValues, setNameValues] = useState([{ localeId: 'en_us', value: '' }]);
  const [industry, setIndustry] = useState('');

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setNameValues(
        initial.localizedName?.values?.length > 0
          ? initial.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: 'en_us', value: '' }]
      );
      setIndustry(initial.industry ?? '');
    } else {
      setNameValues([{ localeId: 'en_us', value: '' }]);
      setIndustry('');
    }
  }, [open, initial?.id]); // primitive id dep avoids re-reset when object ref changes

  const isValid = hasRequiredName(nameValues);
  const isEdit = Boolean(initial);

  const handleSubmit = async () => {
    if (!isValid) return;
    try {
      await onSave({
        localizedName: {
          key: initial?.localizedName?.key ?? uuid(),
          values: nameValues,
        },
        industry,
        isActive: true,
      });
      onClose();
    } catch {
      // global api-error handler shows the toast; modal stays open for retry
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', maxHeight: { xs: '95vh', sm: '85vh' }, m: { xs: 0.5, sm: 2 }, overflow: 'hidden' } }}>
      <DialogTitle sx={{ bgcolor: '#0a326d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          {readOnly ? <LocalText>View Advertiser</LocalText> : isEdit ? <LocalText>Edit Advertiser</LocalText> : <LocalText>New Advertiser</LocalText>}
        </Typography>
        <IconButton size="small" onClick={(e) => { e.currentTarget.blur(); onClose(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

          <SectionLabel required>Brand Name</SectionLabel>
          <DropdownTextField
            label="Brand Name"
            values={nameValues}
            attribute="value"
            onChange={setNameValues}
            disabled={readOnly}
            required={!readOnly}
          />

          <Divider sx={{ my: 1 }} />

          <SectionLabel>Industry</SectionLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g. Sportswear"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            disabled={readOnly}
            sx={{ mb: 2, ...textFieldSx }}
          />

          {/* Active toggle — hidden for now, always saved as active
          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={readOnly}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#0a326d' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#5383b3' },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f' }}>
                <LocalText>Active</LocalText>
              </Typography>
            }
          />
          */}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        {readOnly ? (
          <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
              <LocalText>Cancel</LocalText>
            </Button>
            <Button
              variant="contained"
              disabled={!isValid}
              onClick={handleSubmit}
              sx={{
                bgcolor: '#0a326d', color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: '10px', px: 3,
                boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
                '&:hover': { bgcolor: '#0d3d85' },
                '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
              }}
            >
              {isEdit ? <LocalText>Save Changes</LocalText> : <LocalText>Create Advertiser</LocalText>}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAdvertiserModal;
